import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spin, Alert, DatePicker, Select, Statistic, Divider } from 'antd';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ArrowUpOutlined, UserOutlined, ShoppingOutlined, DollarOutlined } from '@ant-design/icons';
import LayoutPage from '../../../layouts/Main';
import AnaliticsService from '../../../utils/api/analitics';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Paleta de colores EP&O
const COLORS = {
  primary: '#145F9D',
  secondary: '#1890ff',
  success: '#52c41a',
  warning: '#faad14',
  error: '#f5222d',
  background: '#f8f9fa',
  text: '#262626'
};

const CHART_COLORS = [
  '#145F9D', // Azul principal EP&O
  '#1890ff', // Azul secundario
  '#52c41a', // Verde
  '#faad14', // Amarillo
  '#f5222d', // Rojo
  '#722ed1', // Púrpura
  '#13c2c2'  // Cyan
];

const SupplierAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [timeFilter, setTimeFilter] = useState('current_month');
  
  const [salesData, setSalesData] = useState(null);
  const [salesByCategoryData, setSalesByCategoryData] = useState(null);
  const [servicesData, setServicesData] = useState(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, timeFilter]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      
      if (dateRange && dateRange.length === 2) {
        params.start_date = dateRange[0].toISOString();
        params.end_date = dateRange[1].toISOString();
      } else if (timeFilter === 'last_month') {
        const now = new Date();
        const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        params.start_date = firstDayLastMonth.toISOString();
        params.end_date = lastDayLastMonth.toISOString();
      }

      const [salesResponse, salesByCategoryResponse, servicesResponse] = await Promise.all([
        AnaliticsService.Sales(params),
        AnaliticsService.SalesPerMonth(params),
        AnaliticsService.ProjectsSales(params)
      ]);

      setSalesData(salesResponse.data);
      setSalesByCategoryData(salesByCategoryResponse.data);
      setServicesData(servicesResponse.data);

    } catch (err) {
      setError('Error al cargar los datos de analíticas. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Preparar datos para los gráficos
  const prepareLineChartData = () => {
    if (!salesByCategoryData?.sales_by_month) return [];
    
    return salesByCategoryData.sales_by_month.map(monthData => {
      const formattedData = { name: monthData.month };
      
      let totalSales = 0;
      Object.keys(monthData).forEach(key => {
        if (key !== 'month' && typeof monthData[key] === 'number') {
          totalSales += monthData[key];
        }
      });
      
      formattedData.sales = totalSales;
      return formattedData;
    });
  };

  const preparePieChartData = () => {
    if (!salesData?.sales_by_category) return [];
    
    return salesData.sales_by_category.map(category => ({
      name: category.label,
      value: category.value,
      total_orders: category.total_orders
    }));
  };

  const prepareBarChartData = () => {
    if (!salesByCategoryData?.sales_by_month) return [];
    
    const recentMonths = salesByCategoryData.sales_by_month.slice(-6);
    
    return recentMonths.map(monthData => {
      const formattedData = { name: monthData.month };
      
      Object.keys(monthData).forEach(key => {
        if (key !== 'month' && typeof monthData[key] === 'number' && monthData[key] > 0) {
          formattedData[key] = monthData[key];
        }
      });
      
      return formattedData;
    });
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates) {
      setTimeFilter('custom');
    }
  };

  const handleTimeFilterChange = (value) => {
    setTimeFilter(value);
    if (value !== 'custom') {
      setDateRange(null);
    }
  };

  if (loading) {
    return (
      <LayoutPage>
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" tip="Cargando datos de analíticas..." />
        </div>
      </LayoutPage>
    );
  }

  if (error) {
    return (
      <LayoutPage>
        <div style={{ padding: '24px' }}>
          <Alert message="Error" description={error} type="error" showIcon />
        </div>
      </LayoutPage>
    );
  }

  const lineChartData = prepareLineChartData();
  const pieChartData = preparePieChartData();
  const barChartData = prepareBarChartData();
  const barChartCategories = barChartData.length > 0 
    ? Object.keys(barChartData[0]).filter(key => key !== 'name')
    : [];

  return (
    <LayoutPage>
      <div style={{ padding: '24px', backgroundColor: COLORS.background, minHeight: '100vh' }}>
        
        {/* Header con Filtros */}
        <Card 
          style={{ 
            marginBottom: 24, 
            border: `1px solid #d9d9d9`,
            borderRadius: 8
          }}
          bodyStyle={{ padding: '16px 24px' }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <h1 style={{ 
                color: COLORS.primary, 
                margin: 0, 
                fontSize: '28px',
                fontWeight: 600
              }}>
                Dashboard de Analíticas
              </h1>
              <p style={{ 
                color: COLORS.text, 
                margin: '4px 0 0 0',
                opacity: 0.7,
                fontSize: '14px'
              }}>
                Resumen completo de ventas y desempeño
              </p>
            </Col>
            <Col>
              <Row gutter={16} align="middle">
                <Col>
                  <Select 
                    value={timeFilter} 
                    onChange={handleTimeFilterChange}
                    style={{ width: 150 }}
                    size="large"
                  >
                    <Option value="current_month">Mes Actual</Option>
                    <Option value="last_month">Mes Anterior</Option>
                    <Option value="custom">Personalizado</Option>
                  </Select>
                </Col>
                <Col>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    disabled={timeFilter !== 'custom'}
                    format="YYYY-MM-DD"
                    size="large"
                    style={{ width: 250 }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        {/* KPI PRINCIPALES - Lo más importante primero */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card 
              style={{ 
                borderLeft: `4px solid ${COLORS.primary}`,
                borderRadius: 8
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Statistic
                title="Ventas Totales"
                value={salesData?.total_sales || 0}
                precision={2}
                prefix="$"
                valueStyle={{ color: COLORS.primary, fontSize: '24px' }}
                suffix={<ArrowUpOutlined style={{ color: COLORS.success }} />}
              />
              <div style={{ marginTop: 8, fontSize: '12px', color: COLORS.text, opacity: 0.7 }}>
                Período seleccionado
              </div>
            </Card>
          </Col>
          
          <Col span={6}>
            <Card 
              style={{ 
                borderLeft: `4px solid ${COLORS.secondary}`,
                borderRadius: 8
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Statistic
                title="Cliente Principal"
                value={salesData?.top_customers_by_revenue?.[0]?.revenue || 0}
                precision={2}
                prefix="$"
                valueStyle={{ color: COLORS.secondary }}
              />
              <div style={{ marginTop: 8, fontSize: '12px', color: COLORS.text, opacity: 0.7 }}>
                {salesData?.top_customers_by_revenue?.[0]?.name || 'Sin datos'}
              </div>
            </Card>
          </Col>
          
          <Col span={6}>
            <Card 
              style={{ 
                borderLeft: `4px solid ${COLORS.success}`,
                borderRadius: 8
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Statistic
                title="Total Órdenes"
                value={salesData?.sales_by_category?.reduce((acc, cat) => acc + (cat.total_orders || 0), 0) || 0}
                valueStyle={{ color: COLORS.success }}
                prefix={<ShoppingOutlined />}
              />
              <div style={{ marginTop: 8, fontSize: '12px', color: COLORS.text, opacity: 0.7 }}>
                Órdenes procesadas
              </div>
            </Card>
          </Col>
          
          <Col span={6}>
            <Card 
              style={{ 
                borderLeft: `4px solid ${COLORS.warning}`,
                borderRadius: 8
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Statistic
                title="Categoría Top"
                value={salesData?.sales_by_category?.[0]?.value || 0}
                precision={2}
                prefix="$"
                valueStyle={{ color: COLORS.warning }}
              />
              <div style={{ marginTop: 8, fontSize: '12px', color: COLORS.text, opacity: 0.7 }}>
                {salesData?.sales_by_category?.[0]?.label || 'Sin datos'}
              </div>
            </Card>
          </Col>
        </Row>

        <Divider style={{ margin: '32px 0' }} />

        {/* GRÁFICOS PRINCIPALES */}
        <Row gutter={[24, 24]}>
          {/* Tendencia de Ventas - Gráfico más importante */}
          <Col span={12}>
            <Card 
              title="Tendencia de Ventas Mensuales"
              style={{ borderRadius: 8 }}
              bodyStyle={{ padding: '16px' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={lineChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: COLORS.text }}
                  />
                  <YAxis 
                    tick={{ fill: COLORS.text }}
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                  />
                  <Tooltip 
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Ventas']}
                    labelStyle={{ color: COLORS.text }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke={COLORS.primary}
                    strokeWidth={3}
                    dot={{ fill: COLORS.primary, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: COLORS.primary, strokeWidth: 2 }}
                    name="Ventas Totales"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          {/* Distribución por Categoría */}
          <Col span={12}>
            <Card 
              title="Distribución de Ventas por Categoría"
              style={{ borderRadius: 8 }}
              bodyStyle={{ padding: '16px' }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={60}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                    labelLine={false}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`$${Number(value).toLocaleString()}`, name]}
                    labelFormatter={(label) => `Categoría: ${label}`}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* GRÁFICO DE BARRAS - Ventas por Categoría y Mes */}
        <Row style={{ marginTop: 24 }}>
          <Col span={24}>
            <Card 
              title="Ventas Mensuales por Categoría"
              style={{ borderRadius: 8 }}
              bodyStyle={{ padding: '16px' }}
            >
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: COLORS.text }}
                  />
                  <YAxis 
                    tick={{ fill: COLORS.text }}
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`$${Number(value).toLocaleString()}`, name]}
                    labelStyle={{ color: COLORS.text }}
                  />
                  <Legend />
                  {barChartCategories.map((category, index) => (
                    <Bar 
                      key={category} 
                      dataKey={category} 
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      name={category}
                      radius={[2, 2, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* INFORMACIÓN ADICIONAL - Servicios y Estados */}
        {servicesData && (
          <>
            <Divider style={{ margin: '32px 0' }} />
            
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Card 
                  title="Top 5 Servicios por Ingresos"
                  style={{ borderRadius: 8 }}
                  bodyStyle={{ padding: 0 }}
                >
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {servicesData.top_services_by_revenue?.slice(0, 5).map((service, index) => (
                      <div 
                        key={service.id}
                        style={{ 
                          padding: '16px 24px',
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
                            {service.name}
                          </div>
                          <div style={{ fontSize: '12px', color: COLORS.text, opacity: 0.7 }}>
                            Orden: {service.order_id} | Estado: {service.status}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 600, color: COLORS.primary, fontSize: '16px' }}>
                            ${service.revenue?.toLocaleString()}
                          </div>
                          <div style={{ fontSize: '12px', color: COLORS.text, opacity: 0.7 }}>
                            Ingresos
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>

              <Col span={12}>
                <Card 
                  title="Distribución de Órdenes por Estado"
                  style={{ borderRadius: 8 }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={servicesData.services_by_status}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={60}
                        paddingAngle={2}
                        dataKey="count"
                        label={({ status, count }) => `Estado ${status}: ${count}`}
                      >
                        {servicesData.services_by_status?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </LayoutPage>
  );
};

export default SupplierAnalytics;